import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { userLoginRequest, apiConfig } from '../config/authConfig';
import type {
  ChatSession,
  ChatMessage,
  CreateSessionRequest,
  SendMessageRequest,
  ValidateCodeRequest,
  ValidateCodeResponse,
  LinkAccountRequest,
  UpdateProfileRequest,
  CurrentUser,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  PlatformMetrics,
  SystemPromptTemplate,
  SystemPromptTemplateDetail,
  CreateSystemPromptTemplateRequest,
  UpdateSystemPromptTemplateRequest,
  AssignPromptTemplateRequest,
  ApiError,
} from '../types/api';
import type { 
  Device, 
  CreateDeviceRequest, 
  UpdateDeviceRequest,
  UpdateUserProfileRequest,
  ConversationSummary,
  ConversationDetail
} from '../types/admin';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds for AI responses
});

// MSAL instance for token acquisition (user auth)
let msalInstance: PublicClientApplication | null = null;

export const setMsalInstance = (instance: PublicClientApplication) => {
  msalInstance = instance;
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Check for admin token first (stored in localStorage)
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken && config.url?.startsWith('/admin')) {
      config.headers.Authorization = `Bearer ${adminToken}`;
      return config;
    }

    // Otherwise use MSAL for user auth
    if (msalInstance) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...userLoginRequest,
            account: accounts[0],
          });
          config.headers.Authorization = `Bearer ${response.accessToken}`;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            // Token expired, trigger interactive login
            await msalInstance.acquireTokenRedirect(userLoginRequest);
          }
          throw error;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      statusCode: error.response?.status || 500,
      details: error.response?.data?.details,
    };
    return Promise.reject(apiError);
  }
);

// ============ Chat API ============

export const chatApi = {
  // Get all sessions for the current user
  getSessions: async (): Promise<ChatSession[]> => {
    const response = await apiClient.get<ChatSession[]>('/chat/sessions');
    return response.data;
  },

  // Create a new chat session
  createSession: async (request?: CreateSessionRequest): Promise<ChatSession> => {
    const response = await apiClient.post<ChatSession>('/chat/sessions', request || {});
    return response.data;
  },

  // Get messages for a session
  getMessages: async (sessionId: string): Promise<ChatMessage[]> => {
    const response = await apiClient.get<{ id: string; messages: ChatMessage[] }>(`/chat/sessions/${sessionId}`);
    return response.data.messages || [];
  },

  // Send a message
  sendMessage: async (sessionId: string, request: SendMessageRequest): Promise<ChatMessage> => {
    const response = await apiClient.post<ChatMessage>(
      `/chat/sessions/${sessionId}/messages`,
      request
    );
    return response.data;
  },

  // Archive a session
  archiveSession: async (sessionId: string): Promise<void> => {
    await apiClient.post(`/chat/sessions/${sessionId}/archive`);
  },
};

// ============ User API ============

export const userApi = {
  // Validate email and access code (no auth required)
  validateCode: async (request: ValidateCodeRequest): Promise<ValidateCodeResponse> => {
    const response = await apiClient.post<ValidateCodeResponse>('/users/validate-code', request);
    return response.data;
  },

  // Link external identity to an access code
  linkAccount: async (request: LinkAccountRequest): Promise<CurrentUser> => {
    const response = await apiClient.post<CurrentUser>('/users/link', request);
    return response.data;
  },

  // Get current user profile
  getProfile: async (): Promise<CurrentUser> => {
    const response = await apiClient.get<CurrentUser>('/users/me');
    return response.data;
  },

  // Update current user profile
  updateProfile: async (request: UpdateProfileRequest): Promise<CurrentUser> => {
    const response = await apiClient.put<CurrentUser>('/users/me', request);
    return response.data;
  },
};

// ============ Image API ============

export const imageApi = {
  // Upload an image
  upload: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ url: string }>('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.url;
  },
};

// ============ Admin API ============

export const adminApi = {
  // Get all users
  getUsers: async (): Promise<User[]> => {
    const response = await apiClient.get<User[]>('/admin/users');
    return response.data;
  },

  // Get a specific user
  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/admin/users/${id}`);
    return response.data;
  },

  // Create a new user
  createUser: async (request: CreateUserRequest): Promise<User> => {
    const response = await apiClient.post<User>('/admin/users', request);
    return response.data;
  },

  // Update a user
  updateUser: async (id: string, request: UpdateUserRequest): Promise<User> => {
    const response = await apiClient.put<User>(`/admin/users/${id}`, request);
    return response.data;
  },

  // Regenerate access code
  regenerateAccessCode: async (id: string): Promise<{ accessCode: string }> => {
    const response = await apiClient.post<{ accessCode: string }>(
      `/admin/users/${id}/regenerate-code`
    );
    return response.data;
  },

  // Deactivate a user
  deactivateUser: async (id: string): Promise<void> => {
    await apiClient.post(`/admin/users/${id}/deactivate`);
  },

  // Reactivate a user
  reactivateUser: async (id: string): Promise<void> => {
    await apiClient.post(`/admin/users/${id}/reactivate`);
  },

  // Delete a user permanently
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/users/${id}`);
  },

  // Get platform metrics
  getMetrics: async (): Promise<PlatformMetrics> => {
    const response = await apiClient.get<PlatformMetrics>('/admin/metrics');
    return response.data;
  },

  // Get all system prompt templates
  getPromptTemplates: async (): Promise<SystemPromptTemplate[]> => {
    const response = await apiClient.get<SystemPromptTemplate[]>('/admin/prompts');
    return response.data;
  },

  // Device Management
  getUserDevices: async (userId: string): Promise<Device[]> => {
    const response = await apiClient.get<Device[]>(`/admin/users/${userId}/devices`);
    return response.data;
  },

  createDevice: async (userId: string, device: CreateDeviceRequest): Promise<Device> => {
    const response = await apiClient.post<Device>(`/admin/users/${userId}/devices`, device);
    return response.data;
  },

  updateDevice: async (deviceId: string, device: UpdateDeviceRequest): Promise<Device> => {
    const response = await apiClient.put<Device>(`/admin/devices/${deviceId}`, device);
    return response.data;
  },

  deleteDevice: async (deviceId: string): Promise<void> => {
    await apiClient.delete(`/admin/devices/${deviceId}`);
  },

  // Update user profile (AI settings)
  updateUserProfile: async (userId: string, data: UpdateUserProfileRequest): Promise<User> => {
    const response = await apiClient.patch<User>(`/admin/users/${userId}/profile`, data);
    return response.data;
  },

  // System Prompt Management
  getSystemPrompt: async (): Promise<any> => {
    const response = await apiClient.get('/admin/prompt');
    return response.data;
  },

  updateSystemPrompt: async (data: { content: string; name?: string }): Promise<any> => {
    const response = await apiClient.put('/admin/prompt', data);
    return response.data;
  },

  // System Prompt Template CRUD (Multi-Template Management)
  getAllPromptTemplates: async (): Promise<SystemPromptTemplateDetail[]> => {
    const response = await apiClient.get<SystemPromptTemplateDetail[]>('/admin/prompts/all');
    return response.data;
  },

  getPromptTemplate: async (templateId: string): Promise<SystemPromptTemplateDetail> => {
    const response = await apiClient.get<SystemPromptTemplateDetail>(`/admin/prompts/${templateId}`);
    return response.data;
  },

  createPromptTemplate: async (request: CreateSystemPromptTemplateRequest): Promise<SystemPromptTemplateDetail> => {
    const response = await apiClient.post<SystemPromptTemplateDetail>('/admin/prompts', request);
    return response.data;
  },

  updatePromptTemplate: async (templateId: string, request: UpdateSystemPromptTemplateRequest): Promise<SystemPromptTemplateDetail> => {
    const response = await apiClient.put<SystemPromptTemplateDetail>(`/admin/prompts/${templateId}`, request);
    return response.data;
  },

  deletePromptTemplate: async (templateId: string): Promise<void> => {
    await apiClient.delete(`/admin/prompts/${templateId}`);
  },

  setDefaultPromptTemplate: async (templateId: string): Promise<SystemPromptTemplateDetail> => {
    const response = await apiClient.put<SystemPromptTemplateDetail>(`/admin/prompts/${templateId}/default`);
    return response.data;
  },

  // Assign template to user
  assignUserPromptTemplate: async (userId: string, request: AssignPromptTemplateRequest): Promise<void> => {
    await apiClient.put(`/admin/users/${userId}/prompt-template`, request);
  },

  // Conversation Management
  getConversations: async (queryString?: string): Promise<{ 
    conversations: ConversationSummary[]; 
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    matchedKeywords: string[];
    isLimited: boolean;
  }> => {
    const url = queryString ? `/admin/conversations?${queryString}` : '/admin/conversations';
    const response = await apiClient.get<{ 
      conversations: ConversationSummary[]; 
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
      matchedKeywords: string[];
      isLimited: boolean;
    }>(url);
    return response.data;
  },

  getConversationDetail: async (sessionId: string): Promise<ConversationDetail> => {
    const response = await apiClient.get<ConversationDetail>(`/admin/conversations/${sessionId}`);
    return response.data;
  },
};

export default apiClient;
